import React from "react"
import Modal from "react-modal";
import Icon from "../SF/MaterialIcon";
import PropTypes from "prop-types";
import css from "./NewAccessKey.scss"
import NewAccessType from "./NewAccessType";
import StyledButton from "../common/StyledButton";
import Footer from "../common/Footer";
import NewUserCredentials from "./NewUserCredentials";

/**
 * Displays the modal when creating a new access key.
 *
 * Before an access key is created, the modal asks the tenant admin to select an access type, and then confirm the
 * creation by clicking the 'Create' button. Or canceling the request by clicking the 'Cancel' button.
 *
 * If the user clicks 'Create', then an API call is made to create a new IAM user with credentials (access key and
 * secret access key). Then the modal is updated to display the credentials. Keep in mind, once the modal is closed,
 * the tenant admin no longer has access to the secret access key.
 *
 * @param {Object} props - see propTypes
 * @returns {JSX.Element} The modal when creating a new access key
 */
const NewAccessKey = (props) => {

    Modal.setAppElement(document.getElementById("main-center"))

    return (
        <div>
            <StyledButton inverted
                          className={css["button"]}
                          onClick={props.onCancel}
                          data-role="addAccessKey">
                <Icon icon="add_circle"/>
                Create Access Key
            </StyledButton>
            {props.userCreated && <Modal
                isOpen={props.isOpen}
                onRequestClose={() => {props.setIsOpen(!props.isOpen)}}
                className={css['secret-key-modal']}
                overlayClassName={css["overlay"]}
                contentLabel="Sensitive Information">
                <div className={css['container']}>
                    <div className={css['content']}>
                        <header className={css['modal-header']}>
                            <div className={css['header_container']}>
                                <Icon className={css['header']} icon="warning"/>
                                <div className={css['sensitive_information']}>Sensitive Information</div>
                            </div>
                            <StyledButton
                                className={css['close-button']}
                                onClick={() => {props.setIsOpen(!props.isOpen)}}
                                aria-label="Close deep link modal"
                                asLink>
                                <Icon icon='close'/>
                            </StyledButton>
                        </header>
                        <NewUserCredentials
                            accessKey={props.userCredentials.access_key}
                            secretAccessKey={props.userCredentials.secret_key}
                            showSecretKey={props.showSecretKey}
                            onClickShowSecretKey={props.onClickShowSecretKey}
                        />
                    </div>
                </div>
            </Modal>}
            {!props.userCreated && <Modal
                isOpen={props.isOpen}
                onRequestClose={() => {props.setIsOpen(!props.isOpen)}}
                className={css['create-key-modal']}
                overlayClassName={css["overlay"]}
                contentLabel="Sensitive Information">
                <div className={css['container']}>
                    <div className={css['content']}>
                        <header className={css['modal-header']}>
                            <h1>Create Access Key</h1>
                            <StyledButton className={css['close-button']}
                                          onClick={() => {props.setIsOpen(!props.isOpen)}}
                                          aria-label="Close deep link modal"
                                          asLink>
                                <Icon icon='close'/>
                            </StyledButton>
                        </header>
                        <NewAccessType selectedOption={props.selectedOption}
                                       setSelectedOption={props.setSelectedOption}/>
                    </div>
                </div>
                <Footer primaryButton={<StyledButton onClick={props.createUser}>Create</StyledButton>}
                        secondaryButton={<StyledButton asLink onClick={() => {props.onCancel()}}>Cancel</StyledButton>}
                />
            </Modal>}
        </div>
    )
}

NewAccessKey.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    selectedOption: PropTypes.object,
    setSelectedOption: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    createUser: PropTypes.func.isRequired,
    userCreated: PropTypes.bool.isRequired,
    userCredentials: PropTypes.object,
    errorMessage: PropTypes.string,
    showSecretKey: PropTypes.bool.isRequired,
    onClickShowSecretKey: PropTypes.func.isRequired
}

export default NewAccessKey;
